/**
 * A farm's soil probes, worst first: what each reads, what that means against
 * its levels (the server's judgement), when it last reported, and its last
 * week as a sparkline. The Soil tab of the farm page. `SoilSummaryStrip` is the
 * one-line version on the Live tab and the overview's farm cards.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Sprout, WifiOff } from 'lucide-react';
import sensorDevicesService, { type SensorSeries } from '../../services/sensorDevices';
import type { OverviewFarm } from '../../services/irrigationOverview';
import { formatDateTimeInTz } from '../../utils/timezone';
import {
  BAND_FILL,
  STATUS_CHIP,
  STATUS_LABEL,
  channelLabel,
  farmSoilChannels,
  formatReading,
  sensorChannelHref,
  summarise,
  valveName,
  type ChannelRef,
} from '../../utils/soilMoisture';

/** A week of one channel as a line, with its dry side shaded. */
function Sparkline({ points, channelRef }: { points: Array<{ t: number; v: number }>; channelRef: ChannelRef }) {
  const W = 120;
  const H = 32;
  if (points.length < 2) {
    return <span className="text-xs text-gray-400">No week yet</span>;
  }
  const lv = channelRef.channel.levels;
  const values = points.map((p) => p.v).concat(lv ? [lv.dry, lv.refill] : []);
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (max - min < 1e-6) { min -= 1; max += 1; }
  const t0 = points[0].t;
  const t1 = points[points.length - 1].t;
  const x = (t: number) => ((t - t0) / Math.max(1, t1 - t0)) * W;
  const y = (v: number) => H - ((v - min) / (max - min)) * H;
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const higher = channelRef.channel.direction === 'higher_is_drier';
  const dryY = lv ? y(lv.dry) : null;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block max-w-full" aria-hidden="true">
      {dryY != null && (
        <rect
          x={0}
          width={W}
          y={higher ? 0 : dryY}
          height={higher ? Math.max(0, dryY) : Math.max(0, H - dryY)}
          fill={BAND_FILL.dry}
        />
      )}
      <path d={path} fill="none" stroke="#2563eb" strokeWidth={1.5} />
    </svg>
  );
}

export default function SoilMoisturePanel({
  farm,
  timezone,
}: {
  farm: Pick<OverviewFarm, 'sensors'> & Partial<Pick<OverviewFarm, 'controllers'>>;
  timezone: string;
}) {
  const refs = useMemo(() => farmSoilChannels(farm), [farm]);
  const deviceIds = useMemo(() => [...new Set(refs.map((r) => r.deviceId))].sort().join(','), [refs]);
  const [weeks, setWeeks] = useState<Record<string, SensorSeries | null>>({});

  useEffect(() => {
    let active = true;
    const ids = deviceIds ? deviceIds.split(',') : [];
    Promise.all(
      ids.map((id) =>
        sensorDevicesService
          .getSeries(id, { range: 'week' })
          .then((s) => [id, s] as const)
          .catch(() => [id, null] as const)
      )
    ).then((pairs) => {
      if (active) setWeeks(Object.fromEntries(pairs));
    });
    return () => { active = false; };
  }, [deviceIds]);

  if (refs.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
        No soil moisture probes on this farm. A sensor channel of type Soil Moisture (Watermark), on a sensor
        assigned to this farm, shows here.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {refs.map((ref) => {
        const ch = ref.channel;
        const s = weeks[ref.deviceId]?.series.find(
          (x) => x.channel_kind === ch.channel_kind && x.channel_index === ch.channel_index
        );
        const points = (s?.points ?? []).map((p) => ({ t: new Date(p.t).getTime(), v: p.avg }));
        return (
          <Link
            key={`${ref.deviceId}-${ch.channel_kind}-${ch.channel_index}`}
            to={sensorChannelHref(ref.deviceId, ch.channel_kind, ch.channel_index)}
            className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border bg-white p-3 transition hover:shadow-sm ${
              ch.status === 'dry' ? 'border-red-300' : ch.status === 'drying' ? 'border-amber-300' : 'border-gray-200'
            }`}
          >
            <div className="min-w-0 flex-1 basis-48">
              <p className="break-words text-sm font-semibold text-gray-900">{channelLabel(ref)}</p>
              <p className="text-xs text-gray-500">
                {ch.latest_reading_time
                  ? `Last reading ${formatDateTimeInTz(ch.latest_reading_time, timezone)}`
                  : 'Has never reported'}
              </p>
              {ch.stale && (
                <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-amber-700">
                  <WifiOff className="h-3.5 w-3.5 shrink-0" /> Gone quiet: this may not be the soil now.
                </p>
              )}
              {ch.valve && (
                <p className="mt-0.5 text-xs text-gray-600">
                  Watered by {valveName(farm.controllers ?? [], ch.valve) ?? 'a valve on another farm'}
                  {ch.valve_last_run
                    ? ch.valve_last_run.ended_at
                      ? ` · last ran ${formatDateTimeInTz(ch.valve_last_run.started_at, timezone)}`
                      : ' · running now'
                    : ' · no run recorded'}
                </p>
              )}
              {ch.status === 'unset' && (
                <p className="mt-0.5 text-xs text-gray-500">No levels set, so it is never flagged dry.</p>
              )}
            </div>
            <div className="shrink-0">
              <Sparkline points={points} channelRef={ref} />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-lg font-semibold tabular-nums text-gray-900">
                {formatReading(ch.latest_value, ch.type_unit)}
              </span>
              <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_CHIP[ch.status]}`}>
                {STATUS_LABEL[ch.status]}
              </span>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * "2 dry · 1 needs water · 5 OK". Renders nothing for a farm with no soil
 * probes. `href` makes it a link (to the farm's Soil tab).
 */
export function SoilSummaryStrip({
  farm,
  href,
  compact = false,
}: {
  farm: Pick<OverviewFarm, 'sensors'>;
  href?: string;
  compact?: boolean;
}) {
  const sum = summarise(farmSoilChannels(farm).map((r) => r.channel));
  if (sum.total === 0) return null;
  const bad = sum.dry > 0;
  const parts: Array<{ text: string; cls: string }> = [];
  if (sum.dry) parts.push({ text: `${sum.dry} dry`, cls: 'text-red-700 font-semibold' });
  if (sum.drying) parts.push({ text: `${sum.drying} need${sum.drying === 1 ? 's' : ''} water`, cls: 'text-amber-700 font-semibold' });
  if (sum.wet) parts.push({ text: `${sum.wet} wet`, cls: 'text-sky-700' });
  if (sum.ok) parts.push({ text: `${sum.ok} OK`, cls: 'text-green-700' });
  if (sum.unset) parts.push({ text: `${sum.unset} no levels`, cls: 'text-gray-700' });
  if (sum.silent) parts.push({ text: `${sum.silent} quiet`, cls: 'text-amber-700' });

  const body = (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
      {bad ? (
        <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
      ) : (
        <Sprout className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
      )}
      {!compact && <span className="font-medium text-gray-700">Soil moisture:</span>}
      {parts.map((p, i) => (
        <span key={p.text} className={p.cls}>
          {i > 0 && <span className="mr-2 text-gray-300">·</span>}
          {p.text}
        </span>
      ))}
    </span>
  );

  const cls = `flex items-center justify-between gap-2 rounded-xl border px-3 py-2 ${
    bad ? 'border-red-200 bg-red-50' : sum.drying ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'
  }`;
  return href ? (
    <Link to={href} className={`${cls} transition hover:shadow-sm`}>
      {body}
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}
